// Sentinel subprotocol used to carry the auth token through the WS handshake.
// Must match WS_AUTH_SUBPROTOCOL on the backend.
const WS_AUTH_SUBPROTOCOL = 'osavul-ws';

class ReconnectingWebSocket {
  constructor(url, token) {
    this.url = url;
    this.token = token;
    this.reconnectInterval = 1000; // Reconnect interval in milliseconds
    this.websocket = null;
    this.messageHandlers = {};

    this.connect();
  }

  connect() {
    if (!this.token) {
      return;
    }

    // Carry the auth token in the Sec-WebSocket-Protocol header (the only
    // header the browser WS API lets us set) instead of the URL query string,
    // so the token stays out of the URL — and therefore out of access logs,
    // proxy logs and browser history that record request URLs. (It's still a
    // request header, so gateways configured to log headers can see it.)
    // The server reads the non-sentinel value as the token and echoes back the
    // sentinel on accept. Keep this sentinel in sync with the backend
    // (WS_AUTH_SUBPROTOCOL).
    this.websocket = new WebSocket(this.url, [WS_AUTH_SUBPROTOCOL, this.token]);

    this.websocket.onopen = () => {
      // Enforce the auth subprotocol contract: the server must echo back the
      // sentinel on accept. If it negotiated a different (or no) subprotocol,
      // the auth handshake wasn't honored — close instead of treating it as healthy.
      if (this.websocket.protocol !== WS_AUTH_SUBPROTOCOL) {
        console.warn('WebSocket negotiated unexpected subprotocol:', this.websocket.protocol);
        this.websocket.close();
        return;
      }
      console.log('WebSocket connection opened');
    };

    this.websocket.onmessage = (event) => {
      const message = event.data;
      console.log('WebSocket message received:', message);

      try {
        const jsonData = JSON.parse(message);

        const objectType = jsonData?.object_type;
        const handlers = this.messageHandlers[objectType];

        if (handlers && handlers.length > 0) {
          // iterate a copy — a handler may remove itself while we dispatch
          [...handlers].forEach((handler) => handler(jsonData));
        } else {
          console.log(jsonData);
        }
      } catch (error) {
        console.warn('Failed to parse received JSON:', error);
      }
    };

    this.websocket.onclose = (event) => {
      console.log('WebSocket connection closed:', event);

      // Attempt to reconnect
      setTimeout(() => {
        console.log('Reconnecting...');
        this.connect();
      }, this.reconnectInterval);
    };

    this.websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  send(data) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(data);
    } else {
      console.warn('WebSocket not open. Unable to send data:', data);
    }
  }

  close() {
    if (this.websocket) {
      this.websocket.close();
    }
  }

  // Multiple handlers can coexist per object type (e.g. a host page's graph
  // widget and the graph overlay both listening to SOCIAL_GRAPH). Callers that
  // may be mounted simultaneously must pass their own handler to removeHandler;
  // calling removeHandler without a handler clears every handler of that type
  // (the legacy single-handler behavior).
  addHandler(objectType, handler) {
    if (!this.websocket) {
      this.connect();
    }

    if (!this.messageHandlers[objectType]) {
      this.messageHandlers[objectType] = [];
    }
    this.messageHandlers[objectType].push(handler);
  }

  removeHandler(objectType, handler) {
    if (handler === undefined) {
      delete this.messageHandlers[objectType];
      return;
    }
    const handlers = this.messageHandlers[objectType];
    if (!handlers) return;
    const remaining = handlers.filter((item) => item !== handler);
    if (remaining.length > 0) {
      this.messageHandlers[objectType] = remaining;
    } else {
      delete this.messageHandlers[objectType];
    }
  }
}

const token = localStorage.getItem('token');
const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

// The CRA dev-server proxy does not reliably forward WebSocket upgrades (Chrome
// rejects the handshake), so in development the socket connects straight to the
// backend instead. REACT_APP_WS_URL is derived from the package.json "proxy"
// field by scripts/start-dev.js (npm start), so REST and WS always talk to the
// same environment. Websockets aren't subject to CORS and our auth travels in
// the handshake subprotocol, not cookies, so the cross-origin connection is
// fine. The NODE_ENV guard makes sure a stray env var can never redirect
// production builds.
const devWsUrl = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_WS_URL : undefined;
const wsUrl =
  devWsUrl || `${wsProtocol}://${window.location.hostname}:${window.location.port}/API/v1/ws`;

const websocketConnection = new ReconnectingWebSocket(wsUrl, token);
websocketConnection.send('connect');

export default websocketConnection;
